import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMe } from "../../hooks/useMe";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { MenuPortal } from "./MenuPortal";
import { useMenuPosition } from "./useMenuPosition";
import { buildAvatarUrl, getAvatarCacheBuster } from "../../lib/avatar";

export const ProfileMenu = () => {
  const navigate = useNavigate();
  const { user, refresh, clear } = useMe();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuStyle = useMenuPosition(anchorRef, open, { align: "right" });

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch("/logout", { method: "POST" });
    } finally {
      clear();
      await refresh();
      navigate("/login", { replace: true });
    }
  };

  const avatarSrc = buildAvatarUrl(user?.avatarUrl, {
    cacheBuster: getAvatarCacheBuster()
  });

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        ref={anchorRef}
        className="flex items-center gap-2 rounded-full border border-border/50 bg-muted/70 pr-1 text-xs text-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 sm:pr-3"
      >
        <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-muted">
          <img src={avatarSrc} alt="Profile avatar" className="h-8 w-8 object-cover" />
        </span>
        <span className="hidden text-xs text-muted-foreground sm:block">
          {user?.username ?? "User"}
        </span>
      </button>

      {open && (
        <MenuPortal>
          <div
            ref={menuRef}
            style={menuStyle}
            className="z-[100] w-60 max-w-[calc(100vw-1.5rem)] rounded-lg border border-border/50 bg-card/95 p-3 shadow-soft"
          >
            <div className="flex items-center gap-3 px-2 pb-2">
              <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-muted">
                <img src={avatarSrc} alt="Profile avatar" className="h-10 w-10 object-cover" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground">
                  {user?.username ?? "QuickBox User"}
                </div>
                <div className="text-xs text-muted-foreground">{user?.role ?? "User"}</div>
              </div>
            </div>
            <div className="my-2 h-px bg-border/50" />
            <button
              type="button"
              onClick={() => {
                navigate("/profile?tab=profile");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
            >
              <Icon icon="lucide:user" className="h-4 w-4" />
              Profile
            </button>
            <button
              type="button"
              onClick={() => {
                navigate("/profile?tab=security");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
            >
              <Icon icon="lucide:shield" className="h-4 w-4" />
              Security
            </button>
            <button
              type="button"
              onClick={() => {
                navigate("/profile?tab=security&view=sessions");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
            >
              <Icon icon="lucide:monitor" className="h-4 w-4" />
              Sessions
            </button>
            <div className="my-1.5 h-px bg-border/50" />
            <button
              type="button"
              onClick={() => {
                navigate("/profile?tab=notification-settings");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
            >
              <Icon icon="lucide:bell" className="h-4 w-4" />
              Notification Settings
            </button>
            <button
              type="button"
              onClick={() => {
                navigate("/profile?tab=notification-center");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
            >
              <Icon icon="lucide:inbox" className="h-4 w-4" />
              Notification Center
            </button>
            <div className="mt-3">
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={handleLogout}
                icon={<Icon icon="lucide:log-out" className="h-4 w-4 text-foreground" />}
              >
                Logout
              </Button>
            </div>
          </div>
        </MenuPortal>
      )}
    </div>
  );
};
